/**
 * Permissions Mixin
 * 
 * Global Vue mixin that provides permission checking methods to all components.
 * This creates a foundation for role-based UI controls across the application.
 */

import permissionService from '../services/permission.service';

export const permissionsMixin = {
    computed: {
        // Core state getters
        permissions() {
            return this.$store?.getters?.permissions || [];
        },
        userPermissions() {
            return this.$store?.getters?.userPermissions || [];
        },
        permissionsLoaded() {
            return this.$store?.getters?.permissionsLoaded || false;
        },
        userCapabilities() {
            return this.$store?.getters?.userCapabilities || {};
        },
        userIsAdmin() {
            return this.$store?.getters?.userIsAdmin || false;
        },
        authContext() {
            return this.$store?.getters?.authContext || null;
        },

        /**
         * Check if permissions are still loading
         */
        permissionsLoading() {
            return !this.permissionsLoaded;
        },

        /**
         * Get safe permission state (returns false if loading)
         */
        safePermissions() {
            if (!this.permissionsLoaded) {
                return {
                    canEditExperiments: false,
                    canEditOngoingExperiments: false,
                    canSendPushNotifications: false,
                    canSchedulePushNotifications: false,
                    canCreateDraftMessages: false,
                    canDeleteExperiments: false,
                    canActivateExperiments: false,
                    canViewISLKeys: false,
                    canCreateISLKeys: false,
                    canEditISLKeys: false,
                    canDeleteISLKeys: false,
                    isAdmin: false
                };
            }
            return this.userCapabilities;
        },

        // Legacy computed properties for backward compatibility
        // These delegate to the dynamic can() method
        canEditExperiments() {
            return this.can('experiments.edit');
        },
        canEditOngoingExperiments() {
            return this.can('experiments.edit_ongoing');
        },
        canSendPushNotifications() {
            return this.can('messages.send');
        },
        canSchedulePushNotifications() {
            return this.can('messages.schedule');
        },
        canCreateDraftMessages() {
            return this.can('messages.create') || this.can('messages.edit');
        },
        canViewExperiments() {
            return this.can('experiments.view');
        },
        canDeleteExperiments() {
            return this.can('experiments.delete');
        },
        canActivateExperiments() {
            return this.can('experiments.activate');
        },
        canViewISLKeys() {
            return this.can('isl_keys.view');
        },
        canCreateISLKeys() {
            return this.can('isl_keys.create');
        },
        canEditISLKeys() {
            return this.can('isl_keys.edit');
        },
        canDeleteISLKeys() {
            return this.can('isl_keys.delete');
        },
        // Retention messages
        canAccessRetentionMessages() {
            return this.can('retention_messages.view');
        },
        canCreateRetentionMessage() {
            return this.can('retention_messages.create');
        },
        canEditRetentionMessage() {
            return this.can('retention_messages.edit');
        },
        canDeleteRetentionMessage() {
            return this.can('retention_messages.delete');
        },
        canSubmitRetentionMessage() {
            return this.can('retention_messages.submit');
        },
        // Retention images
        canViewRetentionImages() {
            return this.can('retention_images.view');
        },
        canCreateRetentionImage() {
            return this.can('retention_images.create');
        },
        canDeleteRetentionImage() {
            return this.can('retention_images.delete');
        },
        canSubmitRetentionImage() {
            return this.can('retention_images.submit');
        },
        // Retention defaults
        canViewRetentionDefaultMessages() {
            return this.can('retention_default_messages.view');
        },
        canConfigureRetentionDefaultMessages() {
            return this.can('retention_default_messages.configure');
        },
        canDeleteRetentionDefaultMessages() {
            return this.can('retention_default_messages.delete');
        }
        // Admin-only retention ops (realtime URL + perf test) reuse the existing userIsAdmin /
        // admin.all gating — no new flags needed here.
    },

    methods: {
        /**
         * Dynamic permission check - primary method to use
         * @param {string} permission - Permission to check (e.g., 'retention.view')
         * @returns {boolean}
         * 
         * @example
         * this.can('retention.view')
         * this.can('messages.send')
         */
        can(permission) {
            if (!this.permissionsLoaded) {
                return false;
            }
            
            // Admin always has access
            if (this.userIsAdmin) {
                return true;
            }
            
            // Get permissions array safely - use userPermissions getter which returns the array
            const userPermissions = this.$store?.getters?.userPermissions || [];
            
            // Ensure it's an array before checking
            if (!Array.isArray(userPermissions)) {
                console.warn('Permissions is not an array:', userPermissions);
                return false;
            }
            
            return userPermissions.includes(permission);
        },

        /**
         * Check if user has a specific permission (alias for can)
         * @param {string} permission - Permission to check
         * @returns {boolean}
         */
        hasPermission(permission) {
            return this.can(permission);
        },

        /**
         * Check if user has ANY of the specified permissions
         * @param {Array<string>} permissions - Array of permissions to check
         * @returns {boolean}
         * 
         * @example
         * this.canAny(['messages.send', 'messages.schedule'])
         */
        canAny(permissions) {
            if (!Array.isArray(permissions)) {
                return this.can(permissions);
            }
            
            if (!this.permissionsLoaded) {
                return false;
            }
            
            if (this.userIsAdmin) {
                return true;
            }
            
            return permissions.some(permission => this.can(permission));
        },

        /**
         * Check if user has ALL of the specified permissions
         * @param {Array<string>} permissions - Array of permissions to check
         * @returns {boolean}
         * 
         * @example
         * this.canAll(['messages.create', 'messages.send'])
         */
        canAll(permissions) {
            if (!Array.isArray(permissions)) {
                return this.can(permissions);
            }
            
            if (!this.permissionsLoaded) {
                return false;
            }
            
            if (this.userIsAdmin) {
                return true;
            }
            
            return permissions.every(permission => this.can(permission));
        },

        /**
         * Check if user has ANY of the specified permissions (alias)
         * @param {Array<string>} permissions - Array of permissions to check
         * @returns {boolean}
         */
        hasAnyPermission(permissions) {
            return this.canAny(permissions);
        },

        /**
         * Check resource permission with action
         * @param {string} resource - Resource name (e.g., 'retention', 'messages')
         * @param {string} action - Action (e.g., 'view', 'edit', 'create', 'delete')
         * @returns {boolean}
         * 
         * @example
         * this.canAccess('retention', 'view')
         * this.canAccess('messages', 'send')
         */
        canAccess(resource, action) {
            return this.can(`${resource}.${action}`);
        },

        /**
         * Permission checks specific to experiments
         */
        canEditThisExperiment(experiment) {
            if (!this.permissionsLoaded) {
                return false;
            }

            // Can always edit if admin
            if (this.userIsAdmin) {
                return true;
            }

            // Can't edit if no general edit permission
            if (!this.can('experiments.edit')) {
                return false;
            }

            // Check if experiment is ongoing (started and not paused)
            if (experiment && experiment.status === 'started' && !experiment.paused) {
                return this.can('experiments.edit_ongoing');
            }

            return true;
        },

        /**
         * Get reason why experiment can't be edited (for UI tooltips)
         */
        getExperimentEditRestrictionReason(experiment) {
            if (!this.can('experiments.edit')) {
                return 'You do not have permission to edit experiments.';
            }

            if (experiment && experiment.status === 'started' && !experiment.paused) {
                if (!this.can('experiments.edit_ongoing')) {
                    return 'Cannot edit ongoing experiments. Please pause the experiment first or contact an administrator.';
                }
            }

            return '';
        },

        /**
         * Permission checks specific to push notifications
         */
        canSendThisMessage() {
            return this.can('messages.send');
        },

        canScheduleThisMessage() {
            return this.can('messages.schedule');
        },

        canSaveMessageAsDraft() {
            return this.can('messages.create') || this.can('messages.edit');
        },

        /**
         * Get reason why message action is restricted
         */
        getMessageActionRestrictionReason(action) {
            const reasons = {
                'send': 'You do not have permission to send push notifications. You can save as draft instead.',
                'schedule': 'You do not have permission to schedule push notifications. You can save as draft instead.',
                'draft': 'You do not have permission to create draft messages.'
            };
            
            return reasons[action] || 'Action not permitted.';
        },

        /**
         * Utility method to show permission tooltips
         */
        getPermissionTooltip(action, item = null) {
            switch (action) {
                case 'edit_experiment':
                    return this.getExperimentEditRestrictionReason(item);
                case 'send_message':
                    return this.getMessageActionRestrictionReason('send');
                case 'schedule_message':
                    return this.getMessageActionRestrictionReason('schedule');
                case 'save_draft':
                    return this.getMessageActionRestrictionReason('draft');
                default:
                    return permissionService.getPermissionExplanation(action);
            }
        },

        /**
         * Check if button should be disabled based on permissions
         */
        isActionDisabled(action, item = null) {
            if (!this.permissionsLoaded) {
                return true; // Disable everything while loading
            }

            switch (action) {
                case 'edit_experiment':
                    return !this.canEditThisExperiment(item);
                case 'delete_experiment':
                    return !this.can('experiments.delete');
                case 'activate_experiment':
                    return !this.can('experiments.activate');
                case 'view_isl':
                    return !this.can('isl_keys.view');
                case 'create_isl':
                    return !this.can('isl_keys.create');
                case 'edit_isl':
                    return !this.can('isl_keys.edit');
                case 'delete_isl':
                    return !this.can('isl_keys.delete');
                case 'send_message':
                    return !this.canSendThisMessage();
                case 'schedule_message':
                    return !this.canScheduleThisMessage();
                case 'save_draft':
                    return !this.canSaveMessageAsDraft();
                default:
                    return false;
            }
        },

        /**
         * Get button text based on permissions (e.g., "Send" vs "Save Draft")
         */
        getActionButtonText(action, defaultText = '') {
            if (!this.permissionsLoaded) {
                return 'Loading...';
            }

            switch (action) {
                case 'send_or_draft':
                    if (this.canSendThisMessage()) {
                        return 'Send Now';
                    } else if (this.canSaveMessageAsDraft()) {
                        return 'Save as Draft';
                    } else {
                        return 'Not Permitted';
                    }
                case 'schedule_or_draft':
                    if (this.canScheduleThisMessage()) {
                        return 'Schedule';
                    } else if (this.canSaveMessageAsDraft()) {
                        return 'Save as Draft';
                    } else {
                        return 'Not Permitted';
                    }
                default:
                    return defaultText;
            }
        },

        /**
         * Initialize permissions if not loaded
         */
        async initializePermissions() {
            if (!this.permissionsLoaded && this.$store) {
                try {
                    await this.$store.dispatch('fetchUserPermissions');
                } catch (error) {
                    console.warn('Failed to load permissions:', error);
                }
            }
        }
    },

    /**
     * Auto-initialize permissions when component is created
     */
    async created() {
        // Check if store exists and is properly initialized
        if (this.$store && this.$store.getters && this.$store.getters.isAuthenticated && !this.permissionsLoaded) {
            await this.initializePermissions();
        }
    }
}; 